// src/Commoncontext/VarauskalenteritContext.js
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelectedCompany } from './SelectedCompanyContext';
import { fetchCalendarsForCompany, fetchCompanyColor } from '../Api/VarauskalenteritApi';

// Admin-HTTP API (Functions)
import {
  getCalendarById as getCalendarByIdHttp,
  updateCalendarVarattavatajat as updateCalendarVarattavatajatHttp,
  updateCalendarMeta as updateCalendarMetaHttp,
  createCalendar as createCalendarHttp,
} from '../Api/CalendarsAdminApi';

const DAYS = ['Maanantai','Tiistai','Keskiviikko','Torstai','Perjantai','Lauantai','Sunnuntai'];

function hourLabel(h) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:00 - ${pad(h + 1)}:00`;
}

// --- Normalisointi ---
function normalizeVarattavatajat(input) {
  const out = {};
  if (!input) {
    for (let h = 0; h < 24; h++) {
      const lbl = hourLabel(h);
      out[lbl] = {};
      DAYS.forEach((d) => { out[lbl][d] = { open: false, price: 0 }; });
    }
    return out;
  }

  if (Array.isArray(input)) {
    input.forEach((row) => {
      if (!row || !row.Aika) return;
      const lbl = row.Aika;
      out[lbl] = {};
      DAYS.forEach((d) => {
        const cell = row[d] || { open: false, price: 0 };
        out[lbl][d] = { open: !!cell.open, price: Number(cell.price || 0) };
      });
    });
  } else if (typeof input === 'object') {
    Object.keys(input).forEach((lbl) => {
      out[lbl] = {};
      const rec = input[lbl] || {};
      DAYS.forEach((d) => {
        const cell = rec[d] || { open: false, price: 0 };
        out[lbl][d] = { open: !!cell.open, price: Number(cell.price || 0) };
      });
    });
  }

  for (let h = 0; h < 24; h++) {
    const lbl = hourLabel(h);
    if (!out[lbl]) {
      out[lbl] = {};
      DAYS.forEach((d) => { out[lbl][d] = { open: false, price: 0 }; });
    }
  }
  return out;
}

function denormalizeToArray(vmap) {
  const rows = [];
  for (let h = 0; h < 24; h++) {
    const lbl = hourLabel(h);
    const row = { Aika: lbl };
    DAYS.forEach((d) => {
      const cell = vmap?.[lbl]?.[d] || { open: false, price: 0 };
      row[d] = { open: !!cell.open, price: Number(cell.price || 0) };
    });
    rows.push(row);
  }
  return rows;
}

// Luo default-kiinni varattavatajat (map)
function defaultClosedVarattavatajatMap() {
  const m = {};
  for (let h = 0; h < 24; h++) {
    const lbl = hourLabel(h);
    m[lbl] = {};
    DAYS.forEach((d) => { m[lbl][d] = { open: false, price: 0 }; });
  }
  return m;
}

// ---- Context ----
const VarauskalenteritContext = createContext(undefined);

export function VarauskalenteritProvider({ children }) {
  const { selectedCompanyId } = useSelectedCompany();

  const [calendars, setCalendars] = useState([]);          // vain oikeat, ei luonnoksia
  const [companyColors, setCompanyColors] = useState({});
  const [loading, setLoading] = useState(true);

  // details cache: id -> { ...kalenteri, varattavatajat (normalized map), sulut: [] }
  const detailsRef = useRef({});

  // drafts: id("temp-...") -> draft object
  const draftsRef = useRef({});
  const [draftsTick, setDraftsTick] = useState(0); // pakottaa renderin kun drafteja muutetaan

  // Lataa valitun yhtiön kalenterit
  useEffect(() => {
    let active = true;
    async function run() {
      if (!selectedCompanyId) {
        if (active) {
          setCalendars([]);
          setCompanyColors({});
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      try {
        const cal = await fetchCalendarsForCompany(selectedCompanyId);
        const myColor = await fetchCompanyColor(selectedCompanyId);
        if (!active) return;
        setCalendars(cal || []);
        setCompanyColors((prev) => ({ ...prev, [selectedCompanyId]: myColor || '#286e34' }));
      } finally {
        if (active) setLoading(false);
      }
    }
    run();
    return () => { active = false; };
  }, [selectedCompanyId]);

  // --- Värit ---
  async function ensureCompanyColor(companyId) {
    if (!companyId) return;
    if (companyColors[companyId]) return;
    try {
      const c = await fetchCompanyColor(companyId);
      setCompanyColors((prev) => ({ ...prev, [companyId]: c || '#286e34' }));
    } catch {
      setCompanyColors((prev) => ({ ...prev, [companyId]: prev[companyId] || '#286e34' }));
    }
  }
  function getCompanyColor(companyId) {
    return companyColors[companyId] || '#286e34';
  }

  // --- Admin/cache ---
  async function getCalendarByIdCached(calendarId) {
    if (!calendarId) return null;

    // draft?
    if (typeof calendarId === 'string' && calendarId.startsWith('temp-')) {
      const d = draftsRef.current[calendarId];
      if (!d) return null;
      return {
        id: d.id,
        nimi: d.nimi,
        tyyppi: d.tyyppi,
        kuvaus: d.kuvaus,
        rajoitukset: d.rajoitukset,
        varattavatajat: d.varattavatajat, // map
        talonyhtiöt: d.talonyhtiöt,
        sulut: d.sulut || [],
      };
    }

    // cached real
    const cached = detailsRef.current[calendarId];
    if (cached) return cached;

    const data = await getCalendarByIdHttp(calendarId);
    const normalized = {
      ...data,
      varattavatajat: normalizeVarattavatajat(data?.varattavatajat),
      sulut: Array.isArray(data?.sulut) ? data.sulut : [],
    };
    detailsRef.current[calendarId] = normalized;
    return normalized;
  }

  async function updateVarattavatajat(calendarId, varattavatajatArray) {
    if (calendarId.startsWith('temp-')) {
      // päivitä luonnoksen state
      const draft = draftsRef.current[calendarId];
      if (draft) {
        draft.varattavatajat = normalizeVarattavatajat(varattavatajatArray);
        setDraftsTick((x) => x + 1);
      }
      return;
    }
    await updateCalendarVarattavatajatHttp(calendarId, varattavatajatArray);
    detailsRef.current[calendarId] = {
      ...(detailsRef.current[calendarId] || {}),
      varattavatajat: normalizeVarattavatajat(varattavatajatArray),
    };
  }

  async function updateCalendarMeta(calendarId, patch) {
    if (calendarId.startsWith('temp-')) {
      const draft = draftsRef.current[calendarId];
      if (draft) {
        draftsRef.current[calendarId] = { ...draft, ...patch };
        if (Array.isArray(patch.sulut)) {
          draftsRef.current[calendarId].sulut = patch.sulut;
        }
        setDraftsTick((x) => x + 1);
      }
      return;
    }
    await updateCalendarMetaHttp(calendarId, patch);
    detailsRef.current[calendarId] = {
      ...(detailsRef.current[calendarId] || {}),
      ...patch,
      sulut: Array.isArray(patch?.sulut)
        ? patch.sulut
        : (detailsRef.current[calendarId]?.sulut || []),
    };
    setCalendars((prev) =>
      prev.map((c) =>
        c.id === calendarId
          ? { ...c, nimi: patch.nimi ?? c.nimi, tyyppi: patch.tyyppi ?? c.tyyppi }
          : c
      )
    );
  }

  function getSlotConfig(calendarId, date, hour) {
    if (!calendarId || !date || typeof hour !== 'number') {
      return { open: false, price: 0 };
    }
    const cal = (typeof calendarId === 'string' && calendarId.startsWith('temp-'))
      ? draftsRef.current[calendarId]
      : (detailsRef.current[calendarId] || calendars.find(c => c.id === calendarId));
    if (!cal) return { open: false, price: 0 };
    const vmap = cal.varattavatajat ? normalizeVarattavatajat(cal.varattavatajat) : null;
    if (!vmap) return { open: false, price: 0 };
    const dayIdx = date.getDay(); 
    const dowName = ['Sunnuntai','Maanantai','Tiistai','Keskiviikko','Torstai','Perjantai','Lauantai'][dayIdx];
    const lbl = hourLabel(hour);
    const cell = vmap?.[lbl]?.[dowName];
    if (!cell) return { open: false, price: 0 };
    return { open: !!cell.open, price: Number(cell.price || 0) };
  }

  // --- Draftit ---
  function computeNextDraftName(base = 'Uusi kalenteri') {
    const names = new Set([
      ...calendars.map(c => c.nimi?.trim() || ''),
      ...Object.values(draftsRef.current).map(d => d.nimi?.trim() || ''),
    ]);
    if (!names.has(base)) return base;
    let i = 2;
    while (names.has(`${base} ${i}`)) i++;
    return `${base} ${i}`;
  }

  function addDraftCalendar(companyId, overrides = {}) {
    const id = `temp-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
    const nimi = overrides.nimi || computeNextDraftName();
    const draft = {
      id,
      nimi,
      tyyppi: overrides.tyyppi || 'Muu',
      typeId: overrides.typeId || 'muu',
      kuvaus: overrides.kuvaus || '',
      rajoitukset: overrides.rajoitukset || { maxAvoimetPerKayttaja: 0, maxPerKayttajaPerVko: 0 },
      varattavatajat: defaultClosedVarattavatajatMap(), // map
      talonyhtiöt: [companyId],
      sulut: overrides.sulut || [], // ← tukee sulkujen luonnostelua
    };
    draftsRef.current[id] = draft;
    setDraftsTick((x) => x + 1);
    return draft; // palautetaan UI:lle
  }

  function discardDraft(draftId) {
    if (!draftId?.startsWith('temp-')) return;
    delete draftsRef.current[draftId];
    setDraftsTick((x) => x + 1);
  }

  async function createCalendar(payload) {
    // payload: { nimi, tyyppi, kuvaus, rajoitukset, varattavatajatArray, talonyhtiöt, blocks? }
    const res = await createCalendarHttp(payload);
    const newId = res?.id || res?.calendarId;
    if (!newId) throw new Error('createCalendar: id puuttuu vastauksesta');

    // Päivitä details-cacheen juuri luotu kalenteri
    detailsRef.current[newId] = {
      id: newId,
      nimi: payload.nimi,
      tyyppi: payload.tyyppi,
      kuvaus: payload.kuvaus,
      rajoitukset: payload.rajoitukset,
      varattavatajat: normalizeVarattavatajat(payload.varattavatajatArray), // mapiksi
      talonyhtiöt: payload.talonyhtiöt,
      sulut: Array.isArray(payload.blocks) ? payload.blocks : [], // backend tallentaa 'sulut'
    };

    // Päivitä lista
    await refreshCalendars();

    return newId;
  }

  function toArray(varattavatajatMap) {
    return denormalizeToArray(varattavatajatMap);
  }

  async function refreshCalendars() {
    if (!selectedCompanyId) return;
    const cal = await fetchCalendarsForCompany(selectedCompanyId);
    setCalendars(cal || []);
  }

  // Julkaise myös "calendarsWithDrafts" admin-näkymää varten
  const calendarsWithDrafts = useMemo(() => {
    const drafts = Object.values(draftsRef.current);
    return [...calendars, ...drafts];
  }, [calendars, draftsTick]);

  const value = useMemo(() => ({
    // asiakasnäkymä
    calendars,
    loading,
    companyColors,
    ensureCompanyColor,
    getCompanyColor,
    getSlotConfig,

    // admin + luonnokset
    calendarsWithDrafts,
    addDraftCalendar,
    discardDraft,
    createCalendar,

    getCalendarByIdCached,
    updateVarattavatajat,
    updateCalendarMeta,
    refreshCalendars,
    toArray,
  }), [calendars, loading, companyColors, calendarsWithDrafts]);

  return (
    <VarauskalenteritContext.Provider value={value}>
      {children}
    </VarauskalenteritContext.Provider>
  );
}

export function useVarauskalenterit() {
  const ctx = useContext(VarauskalenteritContext);
  if (!ctx) throw new Error('useVarauskalenterit must be used inside VarauskalenteritProvider');
  return ctx;
}
